import { ContactHistoryService } from '../../services/DataService';
import { ContactSubscription } from '../../types/contacts';
import { ContactHistoryStats } from '../../types/analytics';
import { callAdminApi } from './wixAdminApi';
import { ContactsApi } from './contacts';
import type { WixClient } from '../wixClient';

export class ContactHistoryApi implements ContactHistoryService {
  async getContactHistoryStats(contactId: string, period: string): Promise<ContactHistoryStats> {
    try {
      // Get booking history for the contact to calculate stats
      const startDate = new Date();
      const endDate = new Date();
      
      switch (period) {
        case '30':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '60':
          startDate.setDate(startDate.getDate() - 60);
          break;
        case '90':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case 'all':
        default:
          startDate.setFullYear(startDate.getFullYear() - 5); // Go back 5 years for "all time"
          break;
      }

      // Get bookings for this contact in the specified period
      const bookingsResult = await callAdminApi('queryBookings', 'bookings', {
        filter: {
          'contactDetails.contactId': { $eq: contactId },
          startDate: { $gte: startDate.toISOString() },
          endDate: { $lte: endDate.toISOString() },
          status: { $ne: 'CANCELED' }
        }
      });

      const bookings = bookingsResult?.items || [];
      
      const bookingsArray = bookings as Record<string, unknown>[];
      const totalBookings = bookingsArray.length;
      const completedBookings = bookingsArray.filter(b => b.status === 'CONFIRMED' || b.status === 'COMPLETED').length;
      const cancelledBookings = bookingsArray.filter(b => b.status === 'CANCELED').length;

      const totalSpent = bookingsArray.reduce((sum: number, booking) => {
        const price = Number(booking.totalPrice || booking.price || 0);
        return sum + price;
      }, 0);
      
      const totalServices = completedBookings;
      const averageServiceValue = totalServices > 0 ? totalSpent / totalServices : 0;

      return {
        totalServices,
        totalSpent,
        averageServiceValue,
        totalBookings,
        cancelledBookings,
        completedBookings
      };
    } catch (error) {
      console.error('Error getting contact history stats:', error);
      return { 
        totalServices: 0, 
        totalSpent: 0, 
        averageServiceValue: 0, 
        totalBookings: 0, 
        cancelledBookings: 0, 
        completedBookings: 0 
      };
    }
  }

  async getContactSubscriptions(contactId: string): Promise<ContactSubscription[]> {
    try {
      console.log('Getting contact subscriptions for contact ID:', contactId);
      
      // Call the working backend method that uses the Wix client directly
      const subscriptionsResult = await callAdminApi('querySubscriptions', 'subscriptions', {
        filter: {
          'buyer.contactId': { $eq: contactId }
        }
      });
      
      // The result should be an array of subscriptions
      const subscriptions = Array.isArray(subscriptionsResult) ? subscriptionsResult : [];
      console.log(`Received ${subscriptions.length} subscription(s) from backend for contact ${contactId}`);
      
      return subscriptions;
    } catch (error) {
      console.error('Error getting contact subscriptions:', error);
      return [];
    }
  }

  async processInvoicesForSubscriptions(invoicesResult: { invoices?: unknown[] }, contactId: string): Promise<ContactSubscription[]> {
    try {
      console.log('Processing invoices for subscriptions for contact ID:', contactId);
      
      const invoices = (invoicesResult?.invoices || []) as unknown[];
      console.log(`Found ${invoices.length} invoices for contact ${contactId}`);
      
      const allSubscriptions: ContactSubscription[] = [];
      
      // Process invoices for subscriptions
      for (const invoiceItem of invoices) {
        const invoice = invoiceItem as Record<string, unknown>;
        const subscription = this.mapInvoiceToSubscription(invoice, contactId);
        if (subscription) {
          allSubscriptions.push(subscription);
        }
      }
      
      console.log(`Returning ${allSubscriptions.length} subscription(s) from invoices for contact ${contactId}`);
      return allSubscriptions;
    } catch (error) {
      console.error('Error processing invoices for subscriptions:', error);
      return [];
    }
  }

  async processOrdersForSubscriptions(ordersResult: { orders?: unknown[] }, contactId: string): Promise<ContactSubscription[]> {
    try {
      console.log('Processing orders for subscriptions for contact ID:', contactId);
      
      const orders = (ordersResult?.orders || []) as unknown[];
      console.log(`Found ${orders.length} orders for contact ${contactId}`);
      
      const allSubscriptions: ContactSubscription[] = [];
      
      // Process orders for recurring billing patterns
      for (const orderItem of orders) {
        const order = orderItem as Record<string, unknown>;
        const activities = (order.activities as unknown[]) || [];
        
        // Check if this order has recurring billing patterns
        if (this.hasRecurringBillingPattern(order, activities)) {
          const subscription = this.mapOrderToSubscription(order, contactId);
          if (subscription) {
            allSubscriptions.push(subscription);
          }
        }
      }
      
      console.log(`Returning ${allSubscriptions.length} subscription(s) for contact ${contactId}`);
      return allSubscriptions;
    } catch (error) {
      console.error('Error processing orders for subscriptions:', error);
      return [];
    }
  }

  public mapInvoiceToSubscription(invoice: Record<string, unknown>, contactId: string): ContactSubscription | null {
    try {
      const lineItems = (invoice.lineItems as unknown[]) || [];
      const firstLineItem = lineItems[0] as Record<string, unknown> || {};
      const name = (firstLineItem.name as string) || (invoice.title as string) || 'Recurring Service';
      const total = (invoice.total as Record<string, unknown>) || {};
      const amount = parseFloat((total.value as string) || (invoice.amount as string) || '0');
      
      // Determine frequency from invoice metadata or default to monthly
      const frequency = this.determineInvoiceFrequency(invoice);
      const createdDate = new Date((invoice._createdDate as string) || Date.now());
      const status = this.determineInvoiceSubscriptionStatus(invoice);

      return {
        id: (invoice._id as string) || '',
        contactId: contactId,
        name: name,
        type: 'subscription' as const,
        planName: name,
        status: status,
        nextDate: this.calculateNextInvoiceDate(createdDate, frequency, invoice),
        nextBillingDate: this.calculateNextInvoiceDate(createdDate, frequency, invoice),
        startDate: createdDate,
        amount: amount,
        frequency: frequency
      };
    } catch (error) {
      console.error('Error mapping invoice to subscription:', error);
      return null;
    }
  }

  public mapSubscriptionContractToSubscription(contract: Record<string, unknown>, contactId: string): ContactSubscription | null {
    try {
      const contractId = (contract._id as string) || '';
      const status = (contract.status as string) || 'unknown';
      const createdDate = new Date((contract._createdDate as string) || Date.now());
      
      // Extract subscription details from contract
      const subscription = (contract.subscription as Record<string, unknown>) || {};
      const planDetails = (subscription.planDetails as Record<string, unknown>) || {};
      const planName = (planDetails.name as string) || '';
      
      // Extract pricing information
      const pricing = (subscription.pricing as Record<string, unknown>) || {};
      const pricePerCycle = (pricing.pricePerCycle as Record<string, unknown>) || {};
      const amount = parseFloat((pricePerCycle.amount as string) || '0');
      
      // Extract billing cycle information
      const billingCycle = (pricing.billingCycle as Record<string, unknown>) || {};
      const cycleDuration = (billingCycle.cycleDuration as Record<string, unknown>) || {};
      const count = parseInt((cycleDuration.count as string) || '1');
      const unit = (cycleDuration.unit as string) || 'MONTH';
      
      // Convert billing cycle to frequency (must match type definition)
      let frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'one-time' = 'monthly';
      if (unit === 'WEEK') {
        frequency = count === 1 ? 'weekly' : count === 2 ? 'bi-weekly' : 'monthly';
      } else if (unit === 'MONTH') {
        frequency = 'monthly';
      } else if (unit === 'YEAR') {
        frequency = 'monthly'; // Fallback to monthly for yearly
      }
      
      // Calculate next billing date
      const currentCycle = (contract.currentCycle as Record<string, unknown>) || {};
      let nextBillingDateStr: string;
      if (currentCycle.nextBillingDate) {
        nextBillingDateStr = new Date(currentCycle.nextBillingDate as string).toISOString();
      } else {
        nextBillingDateStr = this.calculateNextBillingDate(createdDate, frequency, []);
      }

      return {
        id: contractId,
        contactId: contactId,
        name: planName || 'Subscription Contract',
        type: 'subscription',
        planName: planName || 'Service Plan',
        status: this.mapContractStatusToSubscriptionStatus(status),
        nextDate: nextBillingDateStr,
        nextBillingDate: nextBillingDateStr,
        startDate: createdDate,
        amount: amount,
        frequency: frequency
      };
    } catch (error) {
      console.error('Error mapping subscription contract to subscription:', error);
      return null;
    }
  }

  private mapContractStatusToSubscriptionStatus(contractStatus: string): 'active' | 'inactive' | 'paused' | 'expired' {
    switch (contractStatus.toLowerCase()) {
      case 'active':
        return 'active';
      case 'canceled':
      case 'cancelled':
        return 'expired'; // Map cancelled to expired to match type
      case 'paused':
      case 'suspended':
        return 'paused';
      case 'pending':
      case 'draft':
        return 'inactive';
      default:
        return 'active'; // Default to active for unknown statuses
    }
  }

  // Helcim Integration Methods
  private async getHelcimRecurringInvoices(contactId: string): Promise<Record<string, unknown>[]> {
    try {
      // TODO: Replace with actual Helcim API configuration
      const helcimApiUrl = process.env.HELCIM_API_URL || 'https://api.helcim.com/v2';
      const helcimApiToken = process.env.HELCIM_API_TOKEN;
      
      if (!helcimApiToken) {
        console.error('Helcim API token not configured');
        return [];
      }

      // Map Wix contact ID to Helcim customer ID
      // You'll need to implement this mapping based on your setup
      const helcimCustomerId = await this.getHelcimCustomerIdByContact(contactId);
      
      if (!helcimCustomerId) {
        console.log(`No Helcim customer ID found for contact ${contactId}`);
        return [];
      }

      // Fetch recurring invoices from Helcim
      const response = await fetch(`${helcimApiUrl}/recurring-invoices?customer_id=${helcimCustomerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${helcimApiToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Helcim API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.recurring_invoices || [];
    } catch (error) {
      console.error('Error fetching Helcim recurring invoices:', error);
      return [];
    }
  }

  private async getHelcimCustomerIdByContact(contactId: string): Promise<string | null> {
    try {
      // This is where you'd implement the mapping between Wix contact ID and Helcim customer ID
      // Options:
      // 1. Store mapping in Wix contact custom fields
      // 2. Use email address to match
      // 3. Store mapping in your own database
      
      // For now, let's try to find by email address
      // You'll need to get the contact details first
      const contactsApi = new ContactsApi();
      const contact = await contactsApi.getContactById(contactId);
      
      if (!contact?.emails?.[0]?.email) {
        return null;
      }

      const email = contact.emails[0].email;
      
      // Search Helcim customers by email
      const helcimApiUrl = process.env.HELCIM_API_URL || 'https://api.helcim.com/v2';
      const helcimApiToken = process.env.HELCIM_API_TOKEN;
      
      const response = await fetch(`${helcimApiUrl}/customers?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${helcimApiToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error(`Helcim customer search error: ${response.status}`);
        return null;
      }

      const data = await response.json();
      const customers = data.customers || [];
      
      if (customers.length > 0) {
        return customers[0].customer_id || customers[0].id;
      }

      return null;
    } catch (error) {
      console.error('Error getting Helcim customer ID:', error);
      return null;
    }
  }

  private mapHelcimInvoiceToSubscription(invoice: Record<string, unknown>, contactId: string): ContactSubscription | null {
    try {
      const invoiceId = (invoice.invoice_id || invoice.id) as string;
      const status = (invoice.status as string) || 'active';
      const amount = parseFloat((invoice.amount as string) || '0');
      
      // Extract recurring schedule information
      const recurringSchedule = (invoice.recurring_schedule as Record<string, unknown>) || {};
      const frequency = this.mapHelcimFrequencyToOurs(recurringSchedule.frequency as string);
      
      // Extract dates
      const createdDate = new Date((invoice.created_date as string) || Date.now());
      const nextBillingDate = invoice.next_billing_date 
        ? new Date(invoice.next_billing_date as string).toISOString()
        : this.calculateNextBillingDate(createdDate, frequency, []);

      // Extract description or service name
      const lineItems = (invoice.line_items as Record<string, unknown>[]) || [];
      const description = (invoice.description as string) || 
                         lineItems[0]?.description as string || 
                         'Recurring Service';

      return {
        id: invoiceId,
        contactId: contactId,
        name: description,
        type: 'subscription',
        planName: description,
        status: this.mapHelcimStatusToSubscriptionStatus(status),
        nextDate: nextBillingDate,
        nextBillingDate: nextBillingDate,
        startDate: createdDate,
        amount: amount,
        frequency: frequency
      };
    } catch (error) {
      console.error('Error mapping Helcim invoice to subscription:', error);
      return null;
    }
  }

  private mapHelcimFrequencyToOurs(helcimFrequency: string): 'weekly' | 'bi-weekly' | 'monthly' | 'one-time' {
    switch (helcimFrequency?.toLowerCase()) {
      case 'weekly':
        return 'weekly';
      case 'bi-weekly':
      case 'biweekly':
        return 'bi-weekly';
      case 'monthly':
        return 'monthly';
      case 'yearly':
      case 'annual':
        return 'monthly'; // Fallback to monthly for yearly
      default:
        return 'monthly';
    }
  }

  private mapHelcimStatusToSubscriptionStatus(helcimStatus: string): 'active' | 'inactive' | 'paused' | 'expired' {
    switch (helcimStatus?.toLowerCase()) {
      case 'active':
        return 'active';
      case 'inactive':
      case 'disabled':
        return 'inactive';
      case 'paused':
      case 'suspended':
        return 'paused';
      case 'cancelled':
      case 'canceled':
      case 'terminated':
        return 'expired';
      default:
        return 'active';
    }
  }

  public mapOrderToSubscription(order: Record<string, unknown>, contactId: string): ContactSubscription | null {
    try {
      const lineItems = (order.lineItems as unknown[]) || [];
      const firstLineItem = lineItems[0] as Record<string, unknown> || {};
      const productNameObj = (firstLineItem.productName as Record<string, unknown>) || {};
      const productName = productNameObj.original as string || '';
      const priceSummary = (order.priceSummary as Record<string, unknown>) || {};
      const totalPrice = (priceSummary.total as Record<string, unknown>) || {};
      const activities = (order.activities as unknown[]) || [];
      
      const frequency = this.determineFrequencyFromActivities(activities);
      const createdDate = new Date((order._createdDate as string) || Date.now());
      const status = this.determineSubscriptionStatus(order);

      return {
        id: (order._id as string) || '',
        contactId: contactId,
        name: productName || 'Recurring Service',
        type: 'subscription' as const,
        planName: productName || 'Service Plan',
        status: status,
        nextDate: this.calculateNextBillingDate(createdDate, frequency, activities),
        nextBillingDate: this.calculateNextBillingDate(createdDate, frequency, activities),
        startDate: createdDate,
        amount: parseFloat((totalPrice.amount as string) || '0'),
        frequency: frequency
      };
    } catch (error) {
      console.error('Error mapping order to subscription:', error);
      return null;
    }
  }

  public hasRecurringBillingPattern(order: Record<string, unknown>, activities: unknown[]): boolean {
    // Look for multiple payment activities indicating recurring billing
    const paymentActivities = activities.filter((activity: unknown) => {
      const act = activity as Record<string, unknown>;
      return act.type === 'ORDER_PAID' || 
             act.type === 'INVOICE_ADDED' || 
             act.type === 'INVOICE_SENT' ||
             act.type === 'RECURRING_PAYMENT';
    });
    
    // If there are multiple payment/invoice activities, it suggests recurring billing
    if (paymentActivities.length > 1) {
      return true;
    }
    
    // Check if the order has subscription-related metadata or flags
    const subscriptionIndicators = [
      'subscription', 'recurring', 'monthly', 'weekly', 'annual'
    ];
    
    const orderData = JSON.stringify(order).toLowerCase();
    return subscriptionIndicators.some(indicator => orderData.includes(indicator));
  }

  private determineInvoiceFrequency(invoice: Record<string, unknown>): 'weekly' | 'bi-weekly' | 'monthly' | 'one-time' {
    // Check invoice metadata for frequency indicators
    const invoiceData = JSON.stringify(invoice).toLowerCase();
    
    if (invoiceData.includes('weekly')) {
      return 'weekly';
    } else if (invoiceData.includes('bi-weekly') || invoiceData.includes('biweekly')) {
      return 'bi-weekly';
    } else if (invoiceData.includes('monthly') || invoiceData.includes('recurring')) {
      return 'monthly';
    } else {
      return 'monthly'; // Default for recurring invoices
    }
  }

  private determineInvoiceSubscriptionStatus(invoice: Record<string, unknown>): 'active' | 'inactive' | 'paused' | 'expired' {
    const status = (invoice.status as string)?.toLowerCase();
    const paymentStatus = (invoice.paymentStatus as string)?.toLowerCase();
    
    if (status === 'canceled' || status === 'voided') {
      return 'expired';
    } else if (paymentStatus === 'overdue' || paymentStatus === 'failed') {
      return 'paused';
    } else if (status === 'sent' || status === 'viewed' || paymentStatus === 'paid') {
      return 'active';
    } else {
      return 'inactive';
    }
  }

  private calculateNextInvoiceDate(startDate: Date, frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'one-time', invoice: Record<string, unknown>): string {
    // Check if invoice has a next billing date field
    const nextBilling = invoice.nextBillingDate as string;
    if (nextBilling) {
      return new Date(nextBilling).toISOString().split('T')[0];
    }
    
    // Calculate based on frequency
    const now = new Date();
    const nextDate = new Date(now);
    
    switch (frequency) {
      case 'weekly':
        nextDate.setDate(now.getDate() + 7);
        break;
      case 'bi-weekly':
        nextDate.setDate(now.getDate() + 14);
        break;
      case 'monthly':
        nextDate.setMonth(now.getMonth() + 1);
        break;
      default:
        return ''; // One-time invoices don't have next billing date
    }
    
    return nextDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  }

  private determineFrequencyFromActivities(activities: unknown[]): 'weekly' | 'bi-weekly' | 'monthly' | 'one-time' {
    // Analyze payment intervals from activities
    const paymentDates = activities
      .filter((activity: unknown) => {
        const act = activity as Record<string, unknown>;
        return act.type === 'ORDER_PAID';
      })
      .map((activity: unknown) => {
        const act = activity as Record<string, unknown>;
        return new Date(act._createdDate as string);
      })
      .sort((a, b) => a.getTime() - b.getTime());
    
    if (paymentDates.length < 2) {
      return 'monthly'; // Default
    }
    
    // Calculate average interval between payments
    const intervals = [];
    for (let i = 1; i < paymentDates.length; i++) {
      const daysDiff = (paymentDates[i].getTime() - paymentDates[i-1].getTime()) / (1000 * 60 * 60 * 24);
      intervals.push(daysDiff);
    }
    
    const avgInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    
    if (avgInterval <= 10) {
      return 'weekly';
    } else if (avgInterval <= 20) {
      return 'bi-weekly';
    } else {
      return 'monthly';
    }
  }

  async queryContactSubscriptionsWithClient(client: WixClient, params: { contactId?: string; search?: { filter?: Record<string, unknown> }; filter?: Record<string, unknown> } | string): Promise<ContactSubscription[]> {
    try {
      console.log('Query contact subscriptions with params:', params);
      
      // Extract contactId from various possible parameter formats
      let contactId: string | undefined;
      
      if (typeof params === 'string') {
        contactId = params;
      } else if (params.contactId) {
        contactId = params.contactId;
      } else if (params.search?.filter?.['contactId']) {
        const filterValue = params.search.filter['contactId'] as Record<string, unknown>;
        contactId = filterValue.$eq as string;
      } else if (params.filter?.['contactId']) {
        const filterValue = params.filter['contactId'] as Record<string, unknown>;
        contactId = filterValue.$eq as string;
      } else if (params.filter?.['buyer.contactId']) {
        const filterValue = params.filter['buyer.contactId'] as Record<string, unknown>;
        contactId = filterValue.$eq as string;
      }
      
      if (!contactId) {
        console.error('No contactId found in params:', params);
        throw new Error('contactId is required for subscription queries');
      }
      
      console.log('Searching for recurring invoices via Helcim for contact:', contactId);
      
      try {
        // Fetch recurring invoices from Helcim API
        const helcimInvoices = await this.getHelcimRecurringInvoices(contactId);
        
        if (helcimInvoices.length === 0) {
          console.log(`No Helcim recurring invoices found for contact ${contactId}`);
          return [];
        }
        
        // Map Helcim invoices to our subscription format
        const subscriptions: ContactSubscription[] = [];
        for (const invoice of helcimInvoices) {
          const subscription = this.mapHelcimInvoiceToSubscription(invoice, contactId);
          if (subscription) {
            subscriptions.push(subscription);
          }
        }
        
        console.log(`Returning ${subscriptions.length} subscription(s) from Helcim for contact ${contactId}`);
        return subscriptions;
        
      } catch (error) {
        console.error('Error searching for recurring invoices/orders:', error);
        return [];
      }
    } catch (error) {
      console.error('Error in queryContactSubscriptionsWithClient:', error);
      return [];
    }
  }

  /**
   * Get contact email by contact ID
   */
  private async getContactEmail(contactId: string): Promise<string | null> {
    try {
      const contactsApi = new ContactsApi();
      const contact = await contactsApi.getContactById(contactId);
      
      return contact?.emails?.[0]?.email || null;
    } catch (error) {
      console.error('Error getting contact email:', error);
      return null;
    }
  }

  async queryContactSubscriptions(params: { contactId?: string; search?: { filter?: Record<string, unknown> }; filter?: Record<string, unknown> } | string): Promise<ContactSubscription[]> {
    try {
      console.log('Query contact subscriptions with params:', params);
      
      // Extract contactId from various possible parameter formats
      let contactId: string | undefined;
      
      if (typeof params === 'string') {
        contactId = params;
      } else if (params.contactId) {
        contactId = params.contactId;
      } else if (params.search?.filter?.['buyerInfo.contactId']) {
        const filterValue = params.search.filter['buyerInfo.contactId'] as Record<string, unknown>;
        contactId = filterValue.$eq as string;
      } else if (params.filter?.['buyerInfo.contactId']) {
        const filterValue = params.filter['buyerInfo.contactId'] as Record<string, unknown>;
        contactId = filterValue.$eq as string;
      }
      
      if (!contactId) {
        console.error('No contactId found in params:', params);
        throw new Error('contactId is required for subscription queries');
      }
      
      // This method is now handled by processOrdersForSubscriptions
      return [];
    } catch (error) {
      console.error('Error in queryContactSubscriptions:', error);
      return [];
    }
  }

  private determineSubscriptionStatus(order: Record<string, unknown>): 'active' | 'inactive' | 'paused' | 'expired' {
    const orderStatus = (order.status as string)?.toLowerCase();
    const paymentStatus = (order.paymentStatus as string)?.toLowerCase();
    const activities = (order.activities as unknown[]) || [];
    
    // Check for subscription-specific status indicators
    const hasRecentPayment = activities.some((activity: unknown) => {
      const act = activity as Record<string, unknown>;
      const activityDate = new Date(act._createdDate as string);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return (act.type === 'ORDER_PAID' || act.type === 'RECURRING_PAYMENT') && 
             activityDate > thirtyDaysAgo;
    });
    
    const hasCancellation = activities.some((activity: unknown) => {
      const act = activity as Record<string, unknown>;
      return act.type === 'ORDER_CANCELED' || 
             act.type === 'SUBSCRIPTION_CANCELED' ||
             act.type === 'SUBSCRIPTION_SUSPENDED';
    });
    
    const hasFailedPayment = activities.some((activity: unknown) => {
      const act = activity as Record<string, unknown>;
      return act.type === 'PAYMENT_FAILED' || 
             act.type === 'INVOICE_OVERDUE';
    });
    
    // Determine status based on order state and payment history
    if (hasCancellation || orderStatus === 'canceled') {
      return 'expired';
    } else if (hasFailedPayment || paymentStatus === 'not_paid') {
      return 'paused';
    } else if (orderStatus === 'approved' && paymentStatus === 'paid' && hasRecentPayment) {
      return 'active';
    } else if (orderStatus === 'approved' && paymentStatus === 'paid') {
      return 'active'; // Assume active if paid and approved
    } else {
      return 'inactive';
    }
  }

  private calculateNextBillingDate(startDate: Date, frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'one-time', _activities: unknown[]): string {
    // For now, return a basic calculation - this could be enhanced with actual invoice data
    const now = new Date();
    const nextDate = new Date(now);
    
    switch (frequency) {
      case 'weekly':
        nextDate.setDate(now.getDate() + 7);
        break;
      case 'bi-weekly':
        nextDate.setDate(now.getDate() + 14);
        break;
      case 'monthly':
        nextDate.setMonth(now.getMonth() + 1);
        break;
      default:
        return ''; // One-time orders don't have next billing date
    }
    
    return nextDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  }
}